import React, { useState, useEffect, Fragment } from "react";
import { Modal, Table, Spinner, Badge } from "react-bootstrap";
import axiosInstance from "../../../services/AxiosInstance";
import swal from "sweetalert";
import { format } from "date-fns";

const SuppressionList = () => {
  const [list, setList]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showAddModal, setShow]   = useState(false);
  const [singleNumber, setSingle] = useState("");
  const [bulkText, setBulkText]   = useState("");
  const [note, setNote]           = useState("");
  const [mode, setMode]           = useState("single"); // single | bulk
  const [saving, setSaving]       = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("suppression");
      if (data.status) setList(data.list);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const onAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (mode === "single") {
        const { data } = await axiosInstance.post("suppression", {
          number: singleNumber,
          note,
        });
        if (data.status) {
          swal("Added", "Number added to blocklist.", "success");
          setShow(false);
          setSingle(""); setNote("");
          load();
        } else {
          swal("Error", data.message, "error");
        }
      } else {
        const { data } = await axiosInstance.post("suppression/bulk", {
          numbers: bulkText,
        });
        if (data.status) {
          swal("Bulk Added", `Added ${data.added}, skipped ${data.skipped} of ${data.total}`, "success");
          setShow(false);
          setBulkText("");
          load();
        } else {
          swal("Error", data.message, "error");
        }
      }
    } catch (e) { swal("Error", e.message, "error"); }
    setSaving(false);
  };

  const onRemove = async (item) => {
    const ok = await swal({
      title: "Remove from blocklist?",
      text: `${item.number} will receive messages again.`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!ok) return;
    try {
      const { data } = await axiosInstance.delete(`suppression/${item.id}`);
      if (data.status) { swal("Removed", "", "success"); load(); }
    } catch (e) { swal("Error", e.message, "error"); }
  };

  const reasonBadge = (r) => {
    const map = {
      stop_keyword: { v: "warning", t: "STOP keyword" },
      manual:       { v: "secondary", t: "Manual" },
      blocked:      { v: "danger", t: "Blocked" },
      reported:     { v: "danger", t: "Reported" },
      imported:     { v: "info", t: "Imported" },
    };
    const m = map[r] || map.manual;
    return <Badge variant={m.v}>{m.t}</Badge>;
  };

  return (
    <>
      {/* ── Add Modal ─────────────────────────────────────────────────── */}
      <Modal className="modal fade" show={showAddModal} onHide={() => setShow(false)} size="lg">
        <form onSubmit={onAdd}>
          <div className="modal-header">
            <h4 className="modal-title fs-20">Add to Blocklist</h4>
            <button type="button" className="close" onClick={() => setShow(false)}>
              <span>×</span>
            </button>
          </div>
          <div className="modal-body">
            <div className="row mb-3">
              <div className="col-md-12">
                <div className="btn-group" role="group">
                  <button type="button"
                    className={`btn btn-sm ${mode === "single" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setMode("single")}>
                    Single Number
                  </button>
                  <button type="button"
                    className={`btn btn-sm ${mode === "bulk" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setMode("bulk")}>
                    Bulk Import
                  </button>
                </div>
              </div>
            </div>

            {mode === "single" ? (
              <>
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input type="text" className="form-control" required
                    placeholder="e.g. 919876543210 (with country code)"
                    value={singleNumber} onChange={e => setSingle(e.target.value)} />
                  <small className="text-muted">Country code required, no + or spaces.</small>
                </div>
                <div className="form-group">
                  <label>Note (Optional)</label>
                  <input type="text" className="form-control"
                    placeholder="Why was this number blocked?"
                    value={note} onChange={e => setNote(e.target.value)} />
                </div>
              </>
            ) : (
              <div className="form-group">
                <label>Numbers (one per line, or comma-separated) *</label>
                <textarea className="form-control" rows={8} required
                  placeholder="919876543210&#10;918765432109&#10;917654321098"
                  value={bulkText} onChange={e => setBulkText(e.target.value)} />
                <small className="text-muted">Each number must include country code. Invalid numbers are skipped.</small>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-danger" onClick={() => setShow(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Adding…" : "Add to Blocklist"}
            </button>
          </div>
        </form>
      </Modal>

      {/* ── Page ──────────────────────────────────────────────────────── */}
      <Fragment>
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <div>
                <h4 className="card-title">Suppression List (Blocked Numbers)</h4>
                <small className="text-muted">
                  These numbers will never receive messages from your account.
                  Auto-added when recipients reply STOP, UNSUBSCRIBE, REMOVE, etc.
                </small>
              </div>
              <button className="btn btn-primary" onClick={() => setShow(true)}>
                + Add Number
              </button>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center mt-4">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : list.length === 0 ? (
                <div className="text-center p-4">
                  <p className="text-muted mb-0">No blocked numbers yet. Recipients who reply STOP will be auto-added here.</p>
                </div>
              ) : (
                <Table responsive className="w-100">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Number</th>
                      <th>Reason</th>
                      <th>Keyword</th>
                      <th>Note</th>
                      <th>Blocked At</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((item, i) => (
                      <tr key={item.id}>
                        <td>{i + 1}</td>
                        <td><strong>+{item.number}</strong></td>
                        <td>{reasonBadge(item.reason)}</td>
                        <td>{item.keywordMatched || "—"}</td>
                        <td>{item.note || "—"}</td>
                        <td>{format(new Date(item.createdAt), "dd/MM/yyyy HH:mm")}</td>
                        <td>
                          <button className="btn btn-xs btn-danger" onClick={() => onRemove(item)}>
                            Unblock
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </div>
      </Fragment>
    </>
  );
};

export default SuppressionList;
